import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";

const RICH_TEXT_PURIFY_CONFIG = {
  ALLOWED_ATTR: [
    "href",
    "target",
    "rel",
    "alt",
    "data-attachment-id",
  ],
};

// Use an isolated DOMPurify instance instead of the module-level default export: DOMPurify's
// hook registry is global to whichever instance you call addHook on, so registering the
// img[src]/[srcset]-stripping hook on the default instance would silently affect every other
// screen in the app that also does `import DOMPurify from "dompurify"` and calls
// `DOMPurify.sanitize(...)` - including ones that legitimately want to keep img src. Calling the
// exported factory with `window` (documented DOMPurify pattern for a fresh, independent
// instance) keeps this hook scoped to AI Dev Studio's rich text only.
const aiDevRichTextPurifier = DOMPurify(window);

// img is only ever allowed as a src-less inline placeholder resolved by data-attachment-id;
// a raw src (pasted HTML, hostile markup, ...) is never trusted or rendered.
aiDevRichTextPurifier.addHook("uponSanitizeAttribute", (node, data) => {
  if (node.tagName === "IMG" && (data.attrName === "src" || data.attrName === "srcset")) {
    data.keepAttr = false;
  }
});

export const safeRichHtml = (html) => ({
  __html: aiDevRichTextPurifier.sanitize(html || "", RICH_TEXT_PURIFY_CONFIG),
});

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const escapeAttribute = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

async function resolvePendingImages(container) {
  const images = Array.from(
    container.querySelectorAll("img[data-attachment-id]:not([src])"),
  );
  await Promise.all(
    images.map(async (img) => {
      try {
        const blob = await aiDevelopmentService.attachmentBlob(
          img.getAttribute("data-attachment-id"),
        );
        img.src = URL.createObjectURL(blob);
      } catch {
        img.classList.add("ai-inline-image-error");
      }
    }),
  );
}

export default function RichTextEditor({
  value,
  onChange,
  label,
  required = false,
  onUploadImage,
}) {
  const editor = useRef(null);
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  // Tracks the last HTML this component itself emitted via onChange. The DOMPurify round-trip
  // can normalize the markup slightly (attribute order, quoting, ...), so a plain string compare
  // against the live innerHTML after our own edits can spuriously look like an external change.
  // Re-assigning innerHTML in that case would destroy the live caret position for no reason, so
  // the sync effect only fires for value changes that did not originate from this component.
  const lastEmitted = useRef(value);
  const emit = (html) => {
    lastEmitted.current = html;
    onChange(html);
  };
  useEffect(() => {
    if (!editor.current || value === lastEmitted.current) return;
    if (editor.current.innerHTML !== (value || "")) {
      editor.current.innerHTML = value || "";
      resolvePendingImages(editor.current);
    }
    lastEmitted.current = value;
  }, [value]);
  const command = (name) => {
    editor.current?.focus();
    document.execCommand(name, false);
    emit(aiDevRichTextPurifier.sanitize(editor.current?.innerHTML || "", RICH_TEXT_PURIFY_CONFIG));
  };
  // The file picker / async upload between "user clicks insert" and "image is ready" can
  // steal focus and drop the caret from the editor; without restoring it explicitly the
  // browser falls back to inserting at the very start of the content instead of where the
  // user was typing.
  const captureSelectionRange = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editor.current) return null;
    const range = selection.getRangeAt(0);
    if (!editor.current.contains(range.commonAncestorContainer)) return null;
    return range.cloneRange();
  };
  const restoreSelectionRange = (range) => {
    const selection = window.getSelection();
    selection.removeAllRanges();
    if (range) {
      selection.addRange(range);
      return;
    }
    if (!editor.current) return;
    const end = document.createRange();
    end.selectNodeContents(editor.current);
    end.collapse(false);
    selection.addRange(end);
  };
  const insertImagePlaceholder = (attachmentId, fileName, savedRange) => {
    editor.current?.focus();
    restoreSelectionRange(savedRange);
    const html = `<img class="ai-inline-image" data-attachment-id="${escapeAttribute(
      attachmentId,
    )}" alt="${escapeAttribute(fileName)}">`;
    document.execCommand("insertHTML", false, html);
    const sanitized = aiDevRichTextPurifier.sanitize(editor.current?.innerHTML || "", RICH_TEXT_PURIFY_CONFIG);
    emit(sanitized);
    resolvePendingImages(editor.current);
  };
  const uploadAndInsert = async (file) => {
    if (!onUploadImage || !file || !IMAGE_TYPES.includes(file.type)) return;
    const savedRange = captureSelectionRange();
    setUploading(true);
    try {
      const attachment = await onUploadImage(file);
      insertImagePlaceholder(attachment.id, file.name, savedRange);
    } finally {
      setUploading(false);
    }
  };
  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadAndInsert(file);
  };
  const onDrop = (e) => {
    if (!onUploadImage) return;
    const file = Array.from(e.dataTransfer?.files || []).find((f) =>
      IMAGE_TYPES.includes(f.type),
    );
    if (file) {
      e.preventDefault();
      uploadAndInsert(file);
    }
  };
  const onPaste = (e) => {
    if (!onUploadImage) return;
    const item = Array.from(e.clipboardData?.items || []).find(
      (v) => v.kind === "file" && IMAGE_TYPES.includes(v.type),
    );
    if (item) {
      e.preventDefault();
      uploadAndInsert(item.getAsFile());
    }
  };
  return (
    <div className="ai-rich-field">
      <span>
        {label}
        {required ? " *" : ""}
      </span>
      <div className="ai-rich-toolbar" role="toolbar">
        <button type="button" onClick={() => command("bold")}>
          <b>B</b>
        </button>
        <button type="button" onClick={() => command("italic")}>
          <i>I</i>
        </button>
        <button type="button" onClick={() => command("insertUnorderedList")}>
          • Lista
        </button>
        <button type="button" onClick={() => command("insertOrderedList")}>
          1. Lista
        </button>
        {onUploadImage && (
          <label className="ai-rich-image-button">
            {uploading ? t("common.loading") : t("aiDevelopment.insertImage")}
            <input
              type="file"
              accept={IMAGE_TYPES.join(",")}
              disabled={uploading}
              onChange={onFilePicked}
            />
          </label>
        )}
      </div>
      <div
        ref={editor}
        className="ai-rich-editor"
        contentEditable
        role="textbox"
        aria-multiline="true"
        onInput={(e) =>
          emit(aiDevRichTextPurifier.sanitize(e.currentTarget.innerHTML, RICH_TEXT_PURIFY_CONFIG))
        }
        onDrop={onDrop}
        onPaste={onPaste}
      />
    </div>
  );
}
